/**
 * V98-5 — pure display rules for certification operations (no React, no
 * fetch). Deadline provenance (V98-0-4 / D-98-6): every date cites the rule it
 * came from, or says "verification pending" — never a silent fallback.
 */

export interface DeadlineProvenanceLike {
  rule_id: string | null;
  rule_version: string | null;
  verification: 'verified' | 'verification_pending';
  calendar?: string | null;
  basis?: string | null;
  /** 'workspace': your own SLA / cadence, not a regulator's rule. */
  source?: 'rule' | 'workspace';
}

export interface DeadlineExplanation {
  /** The short label next to the date: "FedRAMP rule", "Your rule" or "Verification pending" — never a rule code (T5-B6). */
  label: string;
  /** The rule id, for a tooltip on the label (never the label itself). */
  title: string | null;
  tone: 'ok' | 'amber';
  /** "How the deadline was set": at most two human lines (T5-B6). */
  lines: string[];
  /** Behind "Show rule": the id, version and raw basis. */
  rule: string[];
}

const RULE_OWNER = (id: string | null | undefined) => (!id ? 'framework' : /^(VDR|SCN|KSI|CDS|FRR|MAS|PVA)-/.test(id) ? 'FedRAMP' : 'framework');

export function howDeadlineWasSet(p: (DeadlineProvenanceLike & { rule_locked?: boolean }) | null | undefined): DeadlineExplanation {
  if (!p || p.verification === 'verification_pending') {
    return {
      label: 'Verification pending',
      title: p?.rule_id ?? null,
      tone: 'amber',
      lines: ['The rule isn’t verified yet, so this date is a placeholder.'],
      rule: [p?.rule_id ? `Rule ${p.rule_id}` : 'No approved rule yet', ...(p?.basis ? [p.basis] : [])],
    };
  }
  if (p.source === 'workspace') {
    return {
      label: 'Your rule', title: null, tone: 'ok',
      lines: ['Set by your own workspace rule, not a regulator’s.', ...(p.basis ? [p.basis] : [])].slice(0, 2),
      rule: [],
    };
  }
  const owner = RULE_OWNER(p.rule_id);
  const second = p.calendar ? 'Business days skip weekends and US federal holidays.' : p.rule_locked ? 'It can only move earlier.' : null;
  return {
    label: owner === 'FedRAMP' ? 'FedRAMP rule' : 'Framework rule', title: p.rule_id ?? null, tone: 'ok',
    lines: [`Set by a ${owner === 'FedRAMP' ? 'FedRAMP' : 'framework'} rule.`, ...(second ? [second] : [])],
    rule: [...(p.rule_id ? [`Rule ${p.rule_id}`] : []), ...(p.rule_version ? [`Version ${p.rule_version}`] : []), ...(p.basis ? [p.basis] : [])],
  };
}

const CLASS_WORD: Record<string, string> = { routine: 'Routine', adaptive: 'Adaptive', transformative: 'Transformative' };
/** The order classes read in (Routine → Adaptive → Transformative). */
export const SCN_CLASS_ORDER = ['routine', 'adaptive', 'transformative'] as const;
export const scnClassWord = (c: string | null | undefined) => (c ? CLASS_WORD[c] ?? c : 'Not decided');

/** Plain-language notice names (the rule id stays in "how the deadline was set"). */
const NOTICE_WORD: Record<string, string> = {
  'SCN-ADP-NTF': 'Adaptive notice · after the change',
  'SCN-TRF-NIP': 'Transformative · initial notice before the change',
  'SCN-TRF-NFP': 'Transformative · final notice before the change',
  'SCN-TRF-NAF': 'Transformative · notice after the change',
  'SCN-TRF-NAV': 'Transformative · notice after verification',
  'SCN-TRF-UPD': 'Transformative · documentation update',
};
export const noticeWord = (ruleId: string) => NOTICE_WORD[ruleId] ?? ruleId;

/**
 * V98-5 review T5-H4: a derived prerequisite reads as the task that clears
 * it ("Send change notice · …"), wherever it is listed (Report tab, Next up,
 * calendar, the approver's card). Manual rows keep their own title.
 */
export function prereqTask(p: { kind: string; title: string }): string {
  const rest = p.title.includes(' · ') ? p.title.slice(p.title.indexOf(' · ') + 3) : p.title;
  if (p.kind === 'scn_notice') return `Send change notice · ${rest}`;
  if (p.kind === 'acceptance_renewal') return `Sign renewal · ${rest}`;
  if (p.kind === 'agency_usage') return `Confirm agency usage · ${rest}`;
  return p.title;
}

export const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
